import React from 'react';
import { Outlet, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { FaHome, FaInfoCircle, FaBuilding, FaUsers, FaEnvelope, FaUserShield, FaSignOutAlt, FaThLarge } from 'react-icons/fa';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.offWhite};
`;

const Sidebar = styled.aside`
  width: 260px;
  background: ${({ theme }) => theme.colors.nearBlack};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  border-right: 2px solid ${({ theme }) => theme.colors.gold};
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.75rem 1.5rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);

  .eyebrow {
    font-size: 0.7rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.goldLight};
  }

  h3 {
    color: ${({ theme }) => theme.colors.white};
    margin: 0.35rem 0 0.5rem;
    font-size: 1.4rem;
    font-family: ${({ theme }) => theme.fonts.heading};
  }

  small {
    color: rgba(255,255,255,0.6);
    font-size: 0.82rem;
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  padding: 1rem 0;
`;

const SidebarLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.85rem 1.75rem;
  color: ${({ $active }) => ($active ? '#fff' : 'rgba(255,255,255,0.72)')};
  font-size: 0.92rem;
  font-weight: 500;
  border-left: 3px solid ${({ theme, $active }) => ($active ? theme.colors.gold : 'transparent')};
  background: ${({ $active }) => ($active ? 'rgba(200,169,81,0.10)' : 'transparent')};
  transition: ${({ theme }) => theme.transitions.base};

  svg { color: ${({ theme }) => theme.colors.gold}; font-size: 0.95rem; }

  &:hover {
    background: rgba(255,255,255,0.05);
    color: #fff;
  }
`;

const LogoutButton = styled.button`
  margin: 1rem 1.5rem 1.75rem;
  padding: 0.75rem 1rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.goldLight};
  border: 1px solid ${({ theme }) => theme.colors.goldDark};
  border-radius: ${({ theme }) => theme.radii.sm};
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  transition: ${({ theme }) => theme.transitions.base};

  &:hover {
    background: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.nearBlack};
    border-color: ${({ theme }) => theme.colors.gold};
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 2.5rem 2.5rem 4rem;
  overflow-y: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 1.5rem;
  }
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const AdminLayout = () => {
  const { user, logout, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) return <Loading>Loading...</Loading>;
  if (!user) return <Navigate to="/admin/login" />;
  if (!isAdmin) return <Navigate to="/" />;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const active = (path) => location.pathname === path;

  return (
    <Container>
      <Sidebar>
        <SidebarHeader>
          <div className="eyebrow">Ehcerex</div>
          <h3>CMS Console</h3>
          <small>Welcome, {user.username}</small>
        </SidebarHeader>
        <SidebarNav>
          <SidebarLink to="/admin" $active={active('/admin')}><FaThLarge /> Dashboard</SidebarLink>
          <SidebarLink to="/admin/home" $active={active('/admin/home')}><FaHome /> Home Page</SidebarLink>
          <SidebarLink to="/admin/about" $active={active('/admin/about')}><FaInfoCircle /> About Page</SidebarLink>
          <SidebarLink to="/admin/companies" $active={location.pathname.startsWith('/admin/companies')}><FaBuilding /> Companies</SidebarLink>
          <SidebarLink to="/admin/team" $active={active('/admin/team')}><FaUsers /> Team</SidebarLink>
          <SidebarLink to="/admin/contact" $active={active('/admin/contact')}><FaEnvelope /> Contact</SidebarLink>
          {user.role === 'superAdmin' && (
            <SidebarLink to="/admin/users" $active={active('/admin/users')}><FaUserShield /> Manage Users</SidebarLink>
          )}
        </SidebarNav>
        <LogoutButton onClick={handleLogout}><FaSignOutAlt /> Logout</LogoutButton>
      </Sidebar>
      <Content>
        <Outlet />
      </Content>
    </Container>
  );
};

export default AdminLayout;
